import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { ethers } from "ethers";
import { BSC_MAINNET, BSC_TESTNET } from "@/lib/web3";
import { toast } from "sonner";

const WalletContext = createContext(null);
const WALLET_PERSIST_KEY = "smarthub_wallet_state";

export function WalletProvider({ children }) {
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [address, setAddress] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [walletType, setWalletType] = useState(null); // metamask | trust | walletconnect
  const restoredRef = useRef(false);

  const isCorrectNetwork = chainId === BSC_MAINNET.chainId || chainId === BSC_TESTNET.chainId;

  // ─── Persist wallet state to localStorage so it survives mobile redirects ───
  useEffect(() => {
    if (address && walletType) {
      try {
        localStorage.setItem(WALLET_PERSIST_KEY, JSON.stringify({
          address,
          walletType,
          chainId,
          ts: Date.now(),
        }));
      } catch (e) { /* ignore */ }
    } else if (restoredRef.current === true) {
      // Only remove after we've already attempted restoration (avoids clearing on first mount)
      try { localStorage.removeItem(WALLET_PERSIST_KEY); } catch (e) {}
    }
  }, [address, walletType, chainId]);

  // ─── On mount: silently restore wallet connection if previously connected ───
  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;

    (async () => {
      try {
        const saved = localStorage.getItem(WALLET_PERSIST_KEY);
        if (!saved) {
          restoredRef.current = true;
          return;
        }
        const parsed = JSON.parse(saved);
        // Stale check — drop saved state older than 24h
        if (Date.now() - (parsed.ts || 0) > 24 * 60 * 60 * 1000) {
          localStorage.removeItem(WALLET_PERSIST_KEY);
          restoredRef.current = true;
          return;
        }
        if (!window.ethereum) {
          restoredRef.current = true;
          return;
        }
        // Use eth_accounts (silent, no prompt) — only reconnects if wallet still has us authorized
        const accs = await window.ethereum.request({ method: "eth_accounts" });
        if (cancelled) return;
        if (accs && accs.length > 0) {
          const bp = new ethers.BrowserProvider(window.ethereum);
          const sg = await bp.getSigner();
          const net = await bp.getNetwork();
          setProvider(bp);
          setSigner(sg);
          setAddress(ethers.getAddress(accs[0]));
          setChainId(Number(net.chainId));
          setWalletType(parsed.walletType || "metamask");
        } else {
          // Wallet no longer has us authorized — clear saved state
          localStorage.removeItem(WALLET_PERSIST_KEY);
        }
      } catch (e) {
        /* silent — restoration is best-effort */
      } finally {
        restoredRef.current = true;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Setup listeners for injected wallets
  useEffect(() => {
    if (typeof window === "undefined" || !window.ethereum) return;
    const onAccounts = (accs) => {
      if (!accs || accs.length === 0) {
        // disconnected externally
        disconnect();
      } else {
        setAddress(ethers.getAddress(accs[0]));
      }
    };
    const onChain = (cid) => {
      const id = typeof cid === "string" ? parseInt(cid, 16) : Number(cid);
      setChainId(id);
    };
    window.ethereum.on?.("accountsChanged", onAccounts);
    window.ethereum.on?.("chainChanged", onChain);
    return () => {
      window.ethereum?.removeListener?.("accountsChanged", onAccounts);
      window.ethereum?.removeListener?.("chainChanged", onChain);
    };
    // eslint-disable-next-line
  }, []);

  const connectInjected = useCallback(async (type) => {
    if (typeof window === "undefined" || !window.ethereum) {
      const friendly = {
        metamask: "MetaMask", trust: "Trust Wallet", binance: "Binance Wallet",
        okx: "OKX Wallet", bitget: "Bitget Wallet", tokenpocket: "TokenPocket",
        imtoken: "imToken", coinbase: "Coinbase Wallet",
      }[type] || "Wallet";
      toast.error(`${friendly} not detected. Open this page inside your wallet's in-app browser.`);
      throw new Error("No injected provider");
    }
    try {
      const bp = new ethers.BrowserProvider(window.ethereum);
      const accs = await bp.send("eth_requestAccounts", []);
      const sg = await bp.getSigner();
      const net = await bp.getNetwork();
      const addr = ethers.getAddress(accs[0]);
      const cid = Number(net.chainId);
      setProvider(bp);
      setSigner(sg);
      setAddress(addr);
      setChainId(cid);
      setWalletType(type || "metamask");
      // Return fresh data so the caller can immediately use it without waiting for React state
      return { provider: bp, signer: sg, address: addr, chainId: cid, signMessage: (m) => sg.signMessage(m) };
    } catch (e) {
      toast.error(e?.shortMessage || e?.message || "Wallet connection failed");
      throw e;
    }
  }, []);

  const connectWalletConnect = useCallback(async () => {
    // We use a lightweight approach: prompt users to use MetaMask Mobile / Trust in-app browser
    // because full WalletConnect v2 requires a projectId. The button still routes through the injected path
    // when available; otherwise it shows a helpful toast.
    if (typeof window !== "undefined" && window.ethereum) {
      return connectInjected("walletconnect");
    }
    toast.message("WalletConnect", {
      description:
        "Open this site inside MetaMask Mobile, Trust Wallet, or any WalletConnect-compatible wallet's in-app browser to connect.",
    });
    throw new Error("WalletConnect requires a wallet browser");
  }, [connectInjected]);

  const disconnect = useCallback(() => {
    setProvider(null);
    setSigner(null);
    setAddress(null);
    setChainId(null);
    setWalletType(null);
    try { localStorage.removeItem(WALLET_PERSIST_KEY); } catch (e) {}
  }, []);

  const switchToBSC = useCallback(async (testnet = false) => {
    if (!window.ethereum) {
      toast.error("No injected wallet found.");
      return false;
    }
    const target = testnet ? BSC_TESTNET : BSC_MAINNET;
    try {
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: target.chainIdHex }],
      });
      return true;
    } catch (err) {
      if (err.code === 4902) {
        try {
          await window.ethereum.request({
            method: "wallet_addEthereumChain",
            params: [{
              chainId: target.chainIdHex,
              chainName: target.name,
              nativeCurrency: { name: "BNB", symbol: "BNB", decimals: 18 },
              rpcUrls: [target.rpc],
              blockExplorerUrls: [target.explorer],
            }],
          });
          return true;
        } catch (e) {
          toast.error("Failed to add BSC network");
          return false;
        }
      }
      toast.error(err.message || "Failed to switch network");
      return false;
    }
  }, []);

  const signMessage = useCallback(async (message) => {
    if (!signer) throw new Error("Wallet not connected");
    return await signer.signMessage(message);
  }, [signer]);

  /**
   * Send BEP20 USDT on BSC to the treasury (admin) wallet.
   * Returns the txHash once the user signs in MetaMask/Trust.
   *
   * @param {string} usdtContract - BEP20 USDT contract address on BSC
   * @param {string} toAddress    - Treasury (admin) wallet address
   * @param {number|string} amount - USDT amount in human units (e.g. 50 for 50 USDT)
   * @param {number} decimals     - USDT decimals on BSC (18)
   */
  const sendUSDT = useCallback(async (usdtContract, toAddress, amount, decimals = 18) => {
    if (!signer) throw new Error("Wallet not connected");

    const value = ethers.parseUnits(String(amount), decimals);

    // ─── Helper: hard-timeout wrapper for any promise ───
    const withTimeout = (p, ms, errMsg) =>
      Promise.race([
        p,
        new Promise((_, rej) => setTimeout(() => rej(new Error(errMsg)), ms)),
      ]);

    // 1) Wallet address (must succeed quickly)
    let myAddr;
    try {
      myAddr = await withTimeout(signer.getAddress(), 8_000, "Wallet not responding (address read).");
    } catch (e) {
      throw new Error("Could not read wallet address. Please reconnect your wallet.");
    }

    // 2) Balance check — fail FAST and CLEARLY if user doesn't have enough USDT.
    //    Critical for UX: $100/$250 packages mysteriously "not working" almost always
    //    = user has only $20-$80 USDT in wallet. We surface this explicitly instead of
    //    letting the wallet silently reject the transaction.
    const ERC20_ABI = [
      "function transfer(address to, uint256 value) public returns (bool)",
      "function balanceOf(address owner) view returns (uint256)",
    ];
    let userBalance = null; // BigInt or null if check failed
    try {
      const usdt = new ethers.Contract(usdtContract, ERC20_ABI, signer);
      userBalance = await withTimeout(usdt.balanceOf(myAddr), 8_000, "balance-check-timeout");
      if (userBalance < value) {
        const have = ethers.formatUnits(userBalance, decimals);
        const need = ethers.formatUnits(value, decimals);
        const haveNum = Number(have);
        const needNum = Number(need);
        throw new Error(
          `Insufficient USDT balance.\nYou have: ${haveNum.toFixed(2)} USDT\nYou need: ${needNum.toFixed(2)} USDT\nShort by: ${(needNum - haveNum).toFixed(2)} USDT`
        );
      }
    } catch (e) {
      // Only re-throw if it's an actual insufficient-balance error.
      // RPC/timeout errors → silently continue; wallet will reject if balance is too low.
      if (String(e?.message || "").startsWith("Insufficient USDT balance")) throw e;
      // else: skip and let the wallet handle it
    }

    // 2b) BNB gas balance check — needs ~0.00006 BNB to send the ERC20 transfer.
    //     (60,000 gas × 1 gwei = 0.00006 BNB; we use a 3× safety buffer = 0.00015 BNB)
    //     This catches the other common "deposit not happening" cause: zero BNB for gas.
    try {
      const bnbBal = await withTimeout(
        signer.provider.getBalance(myAddr),
        6_000,
        "bnb-balance-timeout"
      );
      // Safety buffer: require 0.00015 BNB minimum (~2.5× actual cost)
      const minBNB = 150_000_000_000_000n; // 0.00015 BNB in wei
      if (bnbBal < minBNB) {
        const haveBNB = ethers.formatUnits(bnbBal, 18);
        throw new Error(
          `Insufficient BNB for gas.\nYou have: ${Number(haveBNB).toFixed(6)} BNB\nYou need: ~0.0002 BNB for the transaction fee.\nFund your wallet with a small amount of BNB on BSC Mainnet and retry.`
        );
      }
    } catch (e) {
      if (String(e?.message || "").startsWith("Insufficient BNB")) throw e;
      // else: RPC issue, silently continue — wallet will reject if gas is insufficient
    }

    // 3) Build raw transaction MANUALLY so SafePal mobile never triggers
    //    ethers v6's populateTransaction (which calls getGasPrice / estimateGas — both flaky on SafePal).
    //    ERC20 transfer ABI-encoded data:
    const iface = new ethers.Interface(ERC20_ABI);
    const data = iface.encodeFunctionData("transfer", [toAddress, value]);

    // Fetch nonce + chainId from the wallet provider (NOT a public RPC) with strict timeout.
    let nonce;
    try {
      nonce = await withTimeout(
        signer.provider.getTransactionCount(myAddr, "pending"),
        8_000,
        "nonce-timeout"
      );
    } catch (e) {
      // Fall back to letting wallet pick the nonce (omit field)
      nonce = undefined;
    }

    // BSC is chain 56 (legacy gas only — no EIP-1559)
    // ── GAS CONFIG (TUNED MIN — TARGET ~0.00005 BNB FEE) ─────────────────────
    // USDT transfer on BSC actually consumes ~31-55k gas. We set 60k as a safe
    // ceiling. BSC mainnet's gas price floor dropped from 5 gwei → 3 gwei → 1 gwei
    // after the 2024 update. We use 1 gwei = the cheapest accepted by validators.
    //
    //   60,000 gas × 1 gwei = 60,000 gwei = 0.00006 BNB ≈ $0.04 USD
    //
    // For reference the previous setting (5 gwei × 120k) was 0.0006 BNB = 10× this.
    const GAS_LIMIT = 60_000n;
    const GAS_PRICE = 1_000_000_000n; // 1 gwei

    // ── CRITICAL FIX for Trust Wallet hang ──────────────────────────────────
    // ethers v6's `signer.sendTransaction()` calls TWO RPCs:
    //   1. eth_sendTransaction → returns the tx hash (this part WORKS on Trust)
    //   2. eth_getTransactionByHash → fetch full tx object (this HANGS on Trust
    //      because the tx isn't yet propagated to Trust's in-app RPC)
    // The hang on call #2 is why deposits "spin forever" on Trust Wallet even
    // though the on-chain tx was successfully broadcast.
    //
    // Solution: bypass ethers entirely and use the raw EIP-1193 `eth_sendTransaction`
    // RPC, which returns the tx hash as a string and does NOT make a second call.
    // We return a `{hash}` stub object so callers (Dashboard.jsx) keep working unchanged.
    // ────────────────────────────────────────────────────────────────────────
    const toHex = (v) => "0x" + (typeof v === "bigint" ? v.toString(16) : Number(v).toString(16));
    const rawParams = {
      from: myAddr,
      to: usdtContract,
      data,
      value: "0x0",
      gas: toHex(GAS_LIMIT),
      gasPrice: toHex(GAS_PRICE),
    };
    if (nonce !== undefined && nonce !== null) rawParams.nonce = toHex(Number(nonce));

    // 4) Send via window.ethereum.request → single RPC call, returns just the hash.
    //    Works reliably on Trust Wallet, MetaMask, SafePal, Binance Wallet, OKX, etc.
    const TX_TIMEOUT_MS = 180_000; // 3 minutes for user to approve in mobile wallet
    let txHash;
    try {
      txHash = await withTimeout(
        window.ethereum.request({ method: "eth_sendTransaction", params: [rawParams] }),
        TX_TIMEOUT_MS,
        "Wallet did not respond. Open your wallet app, approve the transaction, and we'll detect it automatically — or paste the tx hash via 'Recover Past Deposit'."
      );
    } catch (e) {
      const m = (e?.message || e?.shortMessage || "").toLowerCase();
      // Some wallets (notably SafePal & older Trust builds) reject when `gas`/`gasPrice` are
      // specified explicitly. Retry minimal — let the wallet pick gas itself.
      if (
        m.includes("could not coalesce") ||
        m.includes("getgasprice") ||
        m.includes("unknown tx type") ||
        m.includes("invalid tx type") ||
        m.includes("invalid transaction") ||
        m.includes("invalid params") ||
        m.includes("gas required exceeds")
      ) {
        const minimalRaw = {
          from: myAddr,
          to: usdtContract,
          data,
          value: "0x0",
        };
        txHash = await withTimeout(
          window.ethereum.request({ method: "eth_sendTransaction", params: [minimalRaw] }),
          TX_TIMEOUT_MS,
          "Wallet did not respond. Please retry."
        );
      } else if (m.includes("user rejected") || m.includes("user denied") || m.includes("user cancel") || e?.code === 4001) {
        // User explicitly rejected — surface a clean message
        const err = new Error("Transaction cancelled in wallet.");
        err.code = 4001;
        throw err;
      } else {
        // Final fallback: try ethers' signer path (covers wallets without window.ethereum.request)
        try {
          const txRequest = {
            to: usdtContract,
            from: myAddr,
            data,
            value: 0n,
            chainId: 56,
            type: 0,
            gasLimit: GAS_LIMIT,
            gasPrice: GAS_PRICE,
          };
          if (nonce !== undefined && nonce !== null) txRequest.nonce = Number(nonce);
          const tx = await withTimeout(signer.sendTransaction(txRequest), TX_TIMEOUT_MS, "Wallet did not respond.");
          return tx; // ethers returns a full tx object with .hash — caller is happy
        } catch {
          throw e;
        }
      }
    }

    if (!txHash || typeof txHash !== "string" || !txHash.startsWith("0x")) {
      throw new Error("Wallet returned an invalid transaction hash. Please retry.");
    }
    // Return a minimal tx-like object so existing callers (Dashboard.jsx) work unchanged
    return { hash: txHash };
  }, [signer]);

  const value = {
    provider, signer, address, chainId, walletType,
    isCorrectNetwork,
    connectInjected, connectWalletConnect, disconnect, switchToBSC, signMessage, sendUSDT,
  };

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

export function useWallet() {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error("useWallet outside provider");
  return ctx;
}
